import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Transforms hymn lines containing karaoke markers like:
 * <track:noteIdx> or <track:i,j,k>
 * into HTML spans: <span class="k-chunk" data-k="|t:i|t:j|">text</span>
 * - Multiple indices supported per tag (melisma)
 * - Track is 1-based as per marker format
 * - Excludes leading/trailing spaces and punctuation from the highlight span
 */
@Pipe({
  name: 'karaokeMarkup',
  standalone: true
})
export class KaraokeMarkupPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value) return '' as any;

    // Fast path: no markers
    if (value.indexOf('<') === -1 || value.indexOf('>') === -1) {
      return value;
    }

    // Parse markers and wrap subsequent text into spans with data attributes until next marker
    const re = /<\s*(\d+)\s*:\s*([0-9,\s]+)\s*>/g; // <track:idx[,idx...]>

    let html = '';
    let lastIdx = 0;
    let currentNotes: string[] | null = null;

    const pushText = (text: string) => {
      if (!text) return;

      // Separate leading/trailing punctuation and whitespace so they aren't highlighted
      // This regex safely isolates the core word/syllable from its surrounding symbols
      const match = text.match(/^([\s.,:;!?'"“‘(\[\-]*)([\s\S]*?)([\s.,:;!?'"”’)\]\-]*)$/);
      const leading = match ? match[1] : '';
      const core = match ? match[2] : text;
      const trailing = match ? match[3] : '';

      // Only wrap the 'core' text in the karaoke span
      if (currentNotes && currentNotes.length && core) {
        const data = '|' + currentNotes.map(n => `t:${n}`).join('|') + '|';
        html += `${this.escapeHtml(leading)}<span class="k-chunk" data-k="${data}">${this.escapeHtml(core)}</span>${this.escapeHtml(trailing)}`;
      } else {
        html += this.escapeHtml(text);
      }
    };

    let m: RegExpExecArray | null;
    while ((m = re.exec(value)) !== null) {
      // Append text before marker
      pushText(value.substring(lastIdx, m.index));

      // Update current notes list from marker
      const track = m[1].trim();
      const indices = m[2]
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(idx => `${track}:${idx}`);
      currentNotes = indices;

      lastIdx = re.lastIndex;
    }

    // Remaining text
    pushText(value.substring(lastIdx));

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
